import { Platform } from "@src/mobile/utils/platform";

export interface OpenInBrowserOptions {
  url: string;
  /** Used only on web. Native opens in the in-app SFSafariViewController / Chrome Custom Tabs. */
  windowName?: string;
}

/**
 * Opens a URL in the platform-appropriate browser surface.
 *
 * - Native: in-app browser (`Browser.open` — SFSafariViewController / Chrome Custom Tabs).
 * - Web: `window.open` in a new tab with `noopener,noreferrer`.
 *
 * For URL opens that need to fetch the URL first, prefer `useOpenAsyncUrl`
 * from `@src/mobile/utils/openUrl` so Safari's popup blocker is handled.
 */
export async function openInBrowser(options: OpenInBrowserOptions): Promise<void> {
  if (!Platform.isMobile) {
    window.open(options.url, options.windowName ?? "_blank", "noopener,noreferrer");
    return;
  }

  const { Browser } = await import("@capacitor/browser");
  await Browser.open({ url: options.url, windowName: options.windowName });
}

/**
 * Closes any currently open in-app browser instance. No-op on web.
 *
 * On Android, Chrome Custom Tabs auto-dismiss when a deep link fires back into
 * the app — calling close() after that throws "No active browser instance".
 * This function swallows that specific case so callers don't have to.
 */
export async function closeInAppBrowser(): Promise<void> {
  if (!Platform.isMobile) {
    return;
  }

  const { Browser } = await import("@capacitor/browser");
  await Browser.close().catch(() => {
    // No active browser instance (e.g. Android auto-dismiss after deep link) — ignore.
  });
}
